import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import {
  Box,
  Button,
  COLORS,
  DarkMode,
  Heading,
  IconButton,
} from '@podiumhq/design-system';
import { useTracking } from '@podiumhq/tracking';

import PodiumLogo from 'components/PodiumLogo';
import useInitIpqs from 'components/ThirdPartyScripts/hooks/useInitIpqs';
import useInitSardine from 'components/ThirdPartyScripts/hooks/useInitSardine';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import { useCountryByIpQuery } from 'hooks/queries/useCountryByIpQuery';
import useChangePage from 'hooks/useChangePage';
import useEventListener from 'hooks/useEventListener';
import { TRIAL_URL } from 'routes/constants';
import { MEDIUM_MEDIA_QUERY_WIDTH } from 'styles';

import EmailField from '../../components/FormFields/EmailField';
import PasswordField from '../../components/FormFields/PasswordField';
import { TrialDisclaimer } from '../../layout';

const CreateAccount = () => {
  const changePage = useChangePage();
  const country = useCountryByIpQuery();
  const { trackEvent } = useTracking();
  const { accountExists, hasValidLogin, inviteExists, isInMarketingIframe } =
    useTrialsContext() || {};

  const [isInView, setIsInView] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useInitIpqs(isInView);
  useInitSardine(isInView);

  const handleSubmit = () => {
    setIsSubmitting(true);

    hasValidLogin(true)
      .then((isValidAccountDetails) => {
        trackEvent('trials.accountInfo.nextClicked', {
          valid: isValidAccountDetails,
        });

        setIsSubmitting(false);

        if (isValidAccountDetails && inviteExists) {
          changePage('user-invite-exists');
        }

        if (isValidAccountDetails && !accountExists && !inviteExists) {
          changePage(`${TRIAL_URL}/profile`);
        }
      })
      .catch((error) => {
        trackEvent('trials.accountInfo.error', {
          error: JSON.stringify(error),
        });
        setIsSubmitting(false);
      });
  };

  const handlePageIsInView = (event) => {
    // TODO: add origin to make sure we are getting the right message
    if (event?.data === 'start trial clicked') {
      setIsInView(true);
    }
  };

  useEffect(() => {
    if (!isInMarketingIframe) {
      setIsInView(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener({
    eventName: 'message',
    handler: isInMarketingIframe ? handlePageIsInView : null,
  });

  useEffect(() => {
    if (isInView) {
      trackEvent('trials.accountInfo.visited');

      country()
        .then((response) => console.log(response))
        .catch((error) => console.error(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  const handleClose = () => {
    window.parent.postMessage('hideCheckoutModal', '*');
  };

  return (
    <DarkMode>
      <Box backgroundColor="black.base" height="100vh">
        {isInMarketingIframe && (
          <CloseButton>
            <IconButton
              onClick={handleClose}
              type="XClose"
              size="md"
              variant="ghost"
              fillColor="silver.light"
            />
          </CloseButton>
        )}

        <Content>
          <PodiumLogo icon={true} invert={true} />
          <Heading
            color="silver.lightest"
            fontSize="2xl"
            fontWeight="semibold"
            padding="24px 0"
          >
            Start your 14 day free trial
          </Heading>

          <EmailField isSubmitting={isSubmitting} />
          <PasswordField />

          <TrialDisclaimer mb="32px" />

          <Button
            fillColor="#1E47F6"
            id="trials-account-info-next-clicked"
            mb="32px"
            onClick={handleSubmit}
            size="lg"
            style={{
              display: 'block',
              width: '100%',
            }}
          >
            Start free trial
          </Button>
        </Content>
      </Box>
    </DarkMode>
  );
};

const Content = styled.div`
  width: 345px;
  margin-right: auto;
  margin-left: auto;
  padding-top: 22vh;
  height: 78vh;
  position: relative;

  @media (max-width: ${MEDIUM_MEDIA_QUERY_WIDTH}px) {
    padding-top: 4vh;
    height: 96vh;
  }
`;

const CloseButton = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  border-radius: 50px;
  align-items: center;
  margin-top: 32px;
  right: 32px;
  position: absolute;
  z-index: 1;

  :hover {
    cursor: pointer;

    button {
      background: ${COLORS.black.lighter};
      border: ${COLORS.black.lighter};
    }
  }

  button {
    background: ${COLORS.black.light};
    border: ${COLORS.black.light};
  }
`;

export default CreateAccount;
